// D5 money legs for the bookings list/detail/dashboard screens and the
// offline desk — a portal-side port of
// `supabase/functions/_shared/booking_money.ts`, which is Deno-only and lives
// outside `packages/shared` (D13: `frontend` doesn't consume
// `packages/shared`, and the Deno file predates any convention for sharing
// money math with the portal). Logic is copied verbatim rather than
// re-derived so the portal never silently disagrees with what
// `booking-qr`/`host-balance`/`redeem-booking` compute — and both copies, plus
// the SQL mirror, are asserted against ONE vector table:
// packages/shared/src/money/booking-money.vectors.json (bookingMoney.vectors.test.ts).
//
// fee leg     = charged_in_app while the fee is held (fee_status is a
//               GENERATED column over payment_status — D5 — so there is no
//               second write path).
// balance leg = total_price − charged_in_app (final fix wave, C1), where
//               charged_in_app = COALESCE(deposit_amount, total_price once a
//               payment landed, else 0). A full charge — every paid tour or
//               restaurant booking — leaves nothing to collect at the desk.
//               Independent of fee status: a refunded fee never changes what
//               the guest still owes the host at the desk.
//
// `balance_recorded_amount_nad` is the CUMULATIVE amount the host has
// recorded so far (host-balance's running ledger sum), subtracted once.
// `balance_status` short-circuits to 0 due when the host's own ledger
// already says nothing is left (`received` / `not_due`), so a reconciled
// booking never shows a stale balance figure from a lagging column.

export type FeeStatus = 'pending' | 'paid' | 'refunded' | 'partially_refunded';

export type BalanceStatus = 'not_due' | 'due' | 'reported' | 'partial' | 'received' | 'not_recorded';

export interface BookingMoneyInput {
  totalPrice: number | string | null | undefined;
  /** The deposit snapshot; NULL when the guest was charged the full price. */
  depositAmount: number | string | null | undefined;
  /** Cumulative total the host has recorded so far (Task 12 ruling). */
  balanceRecordedAmountNad: number | string | null | undefined;
  feeStatus: FeeStatus | string | null | undefined;
  balanceStatus: BalanceStatus | string | null | undefined;
}

export interface BookingMoneyResult {
  totalCents: number;
  feePaidCents: number;
  balanceDueCents: number;
}

/** Integer cents from a NAD amount (numeric columns can arrive as either a
 * JS number or a string via PostgREST). Never negative, never NaN. */
export function toCents(amount: number | string | null | undefined): number {
  if (amount === null || amount === undefined) return 0;
  const n = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

/** Mirror of the GENERATED `bookings.fee_status` column
 * (20260925000006): unpaid | processing | failed | NULL → pending;
 * paid | refund_processing → paid; refunded; partially_refunded. For
 * callers that only hold `payment_status` (desk check-in, the offline
 * manifest). Asserted against the shared vectors. */
export function feeStatusFromPaymentStatus(paymentStatus: string | null | undefined): FeeStatus {
  switch (paymentStatus) {
    case 'paid':
    case 'refund_processing':
      return 'paid';
    case 'refunded':
      return 'refunded';
    case 'partially_refunded':
      return 'partially_refunded';
    default:
      return 'pending';
  }
}

const FEE_HELD_STATUSES = new Set<string>(['paid', 'partially_refunded']);
/** fee_status values at which a payment has landed in-app (payment_status
 * paid | refund_processing | refunded | partially_refunded). */
const CHARGE_LANDED_STATUSES = new Set<string>(['paid', 'refunded', 'partially_refunded']);
const BALANCE_SETTLED_STATUSES = new Set<string>(['received', 'not_due']);

/** I3 — NamibiaGo still holds (some of) the fee: `paid` (incl.
 * refund_processing) or `partially_refunded`. The check-in / guest-QR fee
 * gate. SQL mirror: public.booking_fee_held (20260926000049). */
export function isFeeHeld(feeStatus: string | null | undefined): boolean {
  return feeStatus != null && FEE_HELD_STATUSES.has(feeStatus);
}

function hasDeposit(depositAmount: number | string | null | undefined): boolean {
  return depositAmount !== null && depositAmount !== undefined && depositAmount !== '';
}

/** C1 — what the guest was charged in-app, in cents:
 *   COALESCE(deposit_amount, CASE WHEN a payment landed THEN total_price ELSE 0 END)
 * SQL mirror: public.booking_charged_in_app (20260926000047). */
export function chargedInAppCents(input: Pick<BookingMoneyInput, 'totalPrice' | 'depositAmount' | 'feeStatus'>): number {
  if (hasDeposit(input.depositAmount)) return toCents(input.depositAmount);
  return input.feeStatus != null && CHARGE_LANDED_STATUSES.has(input.feeStatus) ? toCents(input.totalPrice) : 0;
}

/** C1 — what the host collects at the property, in cents: total − charged
 * in-app, floored at 0. SQL mirror: public.booking_balance_owed. */
export function balanceOwedCents(input: Pick<BookingMoneyInput, 'totalPrice' | 'depositAmount' | 'feeStatus'>): number {
  return Math.max(0, toCents(input.totalPrice) - chargedInAppCents(input));
}

/** True when the in-app charge covers the whole price (a full charge, or a
 * deposit ≥ total): the guest has nothing left to pay at the property. */
export function isPaidInFullInApp(input: Pick<BookingMoneyInput, 'totalPrice' | 'depositAmount' | 'feeStatus'>): boolean {
  const charged = chargedInAppCents(input);
  return charged > 0 && charged >= toCents(input.totalPrice);
}

export function computeBookingMoney(input: BookingMoneyInput): BookingMoneyResult {
  const totalCents = toCents(input.totalPrice);
  const recordedCents = toCents(input.balanceRecordedAmountNad);

  const feePaidCents = isFeeHeld(input.feeStatus) ? chargedInAppCents(input) : 0;

  const balanceDueCents =
    input.balanceStatus != null && BALANCE_SETTLED_STATUSES.has(input.balanceStatus)
      ? 0
      : Math.max(0, balanceOwedCents(input) - recordedCents);

  return { totalCents, feePaidCents, balanceDueCents };
}

/** Label for the in-app leg on the booking detail, print sheet and desk
 * panel: a charge that covers the whole price is the guest's full payment,
 * not a fee. */
export function inAppPaidLabel(money: Pick<BookingMoneyResult, 'feePaidCents' | 'totalCents'>): string {
  return money.feePaidCents > 0 && money.feePaidCents >= money.totalCents ? 'Paid in full in-app' : 'Fee already paid';
}
